<template lang="pug">
//- showEmptyMessageがtrueになったときだけ、本来の「表示する予定はありません」を表示可能にする
.calendar-view-wrapper(:class="{ 'calendar-empty-message-ready': showEmptyMessage }")
  div(ref="calendarEl" class="calendar-view")
  //- スマートフォンで予定を取得している間、カレンダーの上にローディングを重ねる
  .calendar-loading(v-if="isCalendarLoading" role="status" aria-live="polite")
    span.calendar-loading-spinner(aria-hidden="true")
    span 読み込み中…
</template>

<script setup lang="ts">
import { onBeforeUnmount, onMounted, ref } from 'vue'
import { Calendar } from '@fullcalendar/core'
//- https://fullcalendar.io/docs/locale
import jaLocale from '@fullcalendar/core/locales/ja'
import dayGridPlugin from '@fullcalendar/daygrid'
import timeGridPlugin from '@fullcalendar/timegrid'
import listPlugin from '@fullcalendar/list'
import axios from 'axios'

const calendarEl = ref<HTMLElement | null>(null)

/**
 * カレンダー読み込み中の表示を管理する
 *
 * FullCalendarは、次の週へ移動した直後に一度空のリストを描画してから、
 * APIへ予定を取りに行く。そのままだと一瞬だけ
 * 「表示する予定はありません」が見えるため、次の2つの状態を使い分ける。
 */

// trueの間、スマートフォンの画面に「読み込み中…」を表示する
const isCalendarLoading = ref(false)

// trueになるまで、FullCalendar標準の「表示する予定はありません」をCSSで隠す
const showEmptyMessage = ref(false)

// 取得完了後、空メッセージを表示可能にするまでのタイマー番号を保存する
// タイマー番号を保存しておくと、次の通信が始まったときに途中で取り消せる
let emptyMessageTimer: number | null = null
let calendar: Calendar | null = null

const baseUrl = (window as any).baseUrl ?? '';
console.log(baseUrl)

/**
 * プロジェクト記事のカレンダー表示種別
 */
type ProjectEventType = 'spot' | 'always' | 'past';

const eventColorMap: Record<ProjectEventType, { backgroundColor: string; borderColor?: string; textColor: string }> = {
  spot: {
    backgroundColor: '#749F54',
    // borderColor: '#fff',
    textColor: '#fff',
  },
  always: {
    backgroundColor: '#F2EBC4',
    textColor: '#3B3B3B',
  },
  past: {
    backgroundColor: '#B8B8B8',
    textColor: '#fff',
  },
}


//- モバイル端末判定
const mobileMediaQuery = '(max-width: 767px)'
// スマートフォンのリストに最初から表示する、1日あたりの記事数
const mobileListEventLimit = 4
const isMobile = () => {
  return window.matchMedia(mobileMediaQuery).matches
}

// listWeek は dayMaxEvents に対応していないため、モバイル時だけ日ごとに省略表示する
const applyMobileListEventLimit = () => {
  if (!calendarEl.value) return

  // FullCalendarが作成したリスト（tableのtbody）を取得する
  const tableBodies = calendarEl.value.querySelectorAll<HTMLTableSectionElement>('.fc-list-table tbody')

  tableBodies.forEach((tableBody) => {
    // 画面の再描画やリサイズ後にも正しく処理できるよう、前回追加した状態を一度元に戻す
    tableBody.querySelectorAll('.mobile-list-more').forEach((row) => row.remove())
    tableBody.querySelectorAll<HTMLElement>('.fc-list-event').forEach((row) => {
      row.classList.remove('mobile-list-event-hidden')
    })

    // PC表示、またはlistWeek以外の表示では省略しない
    if (!isMobile() || calendar?.view.type !== 'listWeek') return

    // 同じ日付に属する記事の行を一時的に入れておく配列
    let eventsForDay: HTMLTableRowElement[] = []

    // 配列に集めた1日分の記事を、4件表示＋「他◯件」に整える
    const limitEventsForDay = () => {
      // 4件以下なら隠す記事がないため、何も追加しない
      if (eventsForDay.length <= mobileListEventLimit) {
        eventsForDay = []
        return
      }

      // slice(4)で5件目以降だけを取り出し、CSSクラスを付けて非表示にする
      const hiddenEvents = eventsForDay.slice(mobileListEventLimit)
      hiddenEvents.forEach((row) => row.classList.add('mobile-list-event-hidden'))

      // FullCalendarのリストはtableなので、「他◯件」も新しい行として作成する
      const moreRow = document.createElement('tr')
      moreRow.className = 'mobile-list-more'

      const moreCell = document.createElement('td')
      moreCell.colSpan = 3

      const moreButton = document.createElement('button')
      moreButton.type = 'button'
      moreButton.className = 'mobile-list-more-button'
      const moreButtonText = `他${hiddenEvents.length}件`
      moreButton.textContent = moreButtonText
      // aria-expandedは、記事が開いているかを支援技術にも伝える属性
      moreButton.setAttribute('aria-expanded', 'false')
      moreButton.addEventListener('click', () => {
        const isExpanded = moreButton.getAttribute('aria-expanded') === 'true'

        // 開いている場合は隠し、閉じている場合は非表示クラスを外す
        hiddenEvents.forEach((row) => {
          row.classList.toggle('mobile-list-event-hidden', isExpanded)
        })
        // 状態とボタンの文字を、次に行う操作に合わせて切り替える
        moreButton.setAttribute('aria-expanded', String(!isExpanded))
        moreButton.textContent = isExpanded ? moreButtonText : '閉じる'
      })

      moreCell.appendChild(moreButton)
      moreRow.appendChild(moreCell)
      // その日の最後の記事の直後に「他◯件」の行を配置する
      eventsForDay[eventsForDay.length - 1].after(moreRow)
      eventsForDay = []
    }

    // 日付行が現れるまで記事行を集め、日付が変わるごとに1日分を処理する
    Array.from(tableBody.children).forEach((row) => {
      if (!(row instanceof HTMLTableRowElement)) return

      if (row.classList.contains('fc-list-day')) {
        limitEventsForDay()
      } else if (row.classList.contains('fc-list-event')) {
        eventsForDay.push(row)
      }
    })

    // 最後の日は次の日付行がないため、ループ終了後に明示的に処理する
    limitEventsForDay()
  })
}

// FullCalendarによるDOMの描画完了を待ってから、省略処理を実行する
const scheduleMobileListEventLimit = () => {
  window.requestAnimationFrame(applyMobileListEventLimit)
}

// カレンダーの表示種別をwindowサイズで変更する
const getCalendarViewType = () => {
  return isMobile() ? 'listWeek' : 'dayGridMonth'
}

// カレンダーのコンテンツの高さを取得する
const getCalendarContentHeight = () => {
  return 'auto'
}

onMounted(async () => {
  if (!calendarEl.value) return

  
  // console.log(projects)
  calendar = new Calendar(calendarEl.value as HTMLElement, {
    plugins: [dayGridPlugin, timeGridPlugin, listPlugin],
    initialView: getCalendarViewType(),
    locale: jaLocale, // 日本語化 https://fullcalendar.io/docs/locale
    displayEventTime: false,
    listDayFormat: { day: 'numeric' },
    listDaySideFormat: { weekday: 'long' },
    views: {
      listWeek: {
        titleFormat: { month: 'long' },
      },
    },
    // PCの月表示では「記事3行＋他◯件リンク1行」を最大4行として扱う
    // 3件まではすべて通常表示し、4件目以降がある場合だけ「他◯件」を表示する
    dayMaxEventRows: 4,
    // https://fullcalendar.io/docs/contentHeight
    contentHeight: getCalendarContentHeight(),
    /**
     * FullCalendarが予定の取得を開始・終了したときに呼ばれる
     * isLoadingがtrueなら取得中、falseなら取得完了を表す
     */
    loading: (isLoading) => {
      // ローディングを重ねるのはスマートフォンだけにする
      isCalendarLoading.value = isMobile() && isLoading

      // 前回開始した150msの待機が残っていたら取り消す
      // 素早く週を連続移動した場合に、古い空メッセージが表示されるのを防ぐ
      if (emptyMessageTimer !== null) {
        window.clearTimeout(emptyMessageTimer)
        emptyMessageTimer = null
      }

      if (isLoading) {
        // 取得開始時は、FullCalendarが先に作る空表示をすぐに隠す
        showEmptyMessage.value = false
        return
      }

      // 取得完了直後は、FullCalendarがまだ予定を画面へ反映している途中の場合がある
      // 150ms待ってから空メッセージを表示可能にすると、一瞬のちらつきを防げる
      emptyMessageTimer = window.setTimeout(() => {
        showEmptyMessage.value = true
        emptyMessageTimer = null
      }, 150)
    },
    // https://fullcalendar.io/docs/windowResize
    windowResize: () => {
      if (!calendar) return

      const nextViewType = getCalendarViewType()

      if (calendar.view.type !== nextViewType) {
        calendar.changeView(nextViewType)
      }

      // calendar.setOption('headerToolbar', getHeaderToolbar())
      calendar.setOption('contentHeight', getCalendarContentHeight())
      scheduleMobileListEventLimit()
    },

    datesSet: scheduleMobileListEventLimit,
    eventsSet: scheduleMobileListEventLimit,

    
    events: async (fetchInfo, successCallback, failureCallback) => {
      try {
        // console.log('FullCalendarが必要としている開始日', fetchInfo.startStr)
        // console.log('FullCalendarが必要としている終了日', fetchInfo.endStr)
        const projects = await fetchProjects(
          fetchInfo.startStr,
          fetchInfo.endStr,
        )

        const events = projects.filter((project: any) => project.event_start_at || project.publish_at).map((project: any) => {
          const hasEventPeriod = project.event_start_at && project.event_end_at;
          const eventType = getProjectEventType(project);
          const color = eventColorMap[eventType];
          return {
            id: String(project.id),
            title: project.title,
            start: project.event_start_at ?? project.publish_at,
            // FullCalendarのallDayイベントではendが「表示に含まれない」ため、
            // 開催終了日の翌日を渡して、終了日当日までカレンダーに表示する
            end: hasEventPeriod ? getCalendarExclusiveEnd(project.event_end_at) : fetchInfo.endStr,
            allDay: true,
            backgroundColor: color.backgroundColor,
            borderColor: color.borderColor,
            textColor: color.textColor,
            url: project.external_url || `${baseUrl}/project/${project.id}`,
            extendedProps: {
              project,
              eventType,
              isAlwaysOpen: eventType === 'always',
              isPast: eventType === 'past',
            },
          }
        })

        // console.log('FullCalendar用に変換したevents', events)
        successCallback(events)
      } catch (error) {
        console.error('events取得または変換に失敗しました:', error)
        failureCallback(error as Error)
      }
    },

    // 記事の表示順を設定
    // 開催中（spot）→ 常時掲載（always）→ 終了済み（past）の順にする
    //- https://fullcalendar.io/docs/eventOrder
    eventOrder: (eventA: any, eventB: any) => {
      const eventTypeOrder: Record<ProjectEventType, number> = {
        spot: 0,
        always: 1,
        past: 2,
      }
      const eventAType = eventA.extendedProps.eventType as ProjectEventType
      const eventBType = eventB.extendedProps.eventType as ProjectEventType
      return eventTypeOrder[eventAType] - eventTypeOrder[eventBType]
    },
    // PCの月表示で、レイアウトを詰めるためにFullCalendarが順番を入れ替えるのを防ぐ
    // trueにすると、上のeventOrderで指定した順番が厳密に守られる
    eventOrderStrict: true,

    // 記事のツールチップを設定
    //- https://fullcalendar.io/docs/event-render-hooks#argument
    eventDidMount: (info) => {
      // スマートフォンではツールチップを作成しない
      if (isMobile()) return

      info.el.setAttribute(
        'uk-tooltip',
        `title: ${info.event.title}; pos: top`,
      )

      const UIkit = (window as any).UIkit
      if (UIkit) {
        UIkit.tooltip(info.el)
      }
    },

    dayCellContent: (arg) => {
      // console.log('dayCellContent', arg)
      return arg.date.getDate()
    },

    //- 記事にクラス名を付与する
    //- https://fullcalendar.io/docs/event-render-hooks
    eventClassNames: (arg) => {
      const eventType = arg.event.extendedProps.eventType
      return eventType
    },

    // 外部リンクの記事は、記事一覧と同様に新しいタブで開く
    eventClick: (info) => {
      const externalUrl = info.event.extendedProps.project?.external_url
      if (!externalUrl) return

      info.jsEvent.preventDefault()
      window.open(externalUrl, '_blank', 'noopener,noreferrer')
    },
    // eventColor: '#378006'
  })

  calendar.render()
})

onBeforeUnmount(() => {
  // ページを離れた後にタイマーの処理が実行されないよう、残っていれば解除する
  if (emptyMessageTimer !== null) {
    window.clearTimeout(emptyMessageTimer)
    emptyMessageTimer = null
  }

  if (calendar) {
    calendar.destroy()
    calendar = null
  }
})

/**
 * プロジェクト記事のカレンダー表示種別を判定する
 */
const getProjectEventType = (project: any): ProjectEventType => {
  // 終了日が未入力のものは常時掲載（落ち着いた色）として扱う
  if (!project.event_end_at) {
    return 'always'
  }

  const eventEndDate = parseProjectDateTime(project.event_end_at, true)

  // ① 最初に終了済みか判定する。長期イベントでも、終了後は灰色にする
  if (eventEndDate && eventEndDate.getTime() < Date.now()) {
    return 'past'
  }

  const eventStartDate = project.event_start_at
    ? parseProjectDateTime(project.event_start_at)
    : null

  if (eventStartDate && eventEndDate) {
    // 時刻の影響を受けないよう、開始日と終了日の午前0時同士で期間を計算する
    const startDay = new Date(eventStartDate)
    const endDay = new Date(eventEndDate)
    startDay.setHours(0, 0, 0, 0)
    endDay.setHours(0, 0, 0, 0)

    const millisecondsPerDay = 1000 * 60 * 60 * 24
    const eventPeriodDays = Math.round(
      (endDay.getTime() - startDay.getTime()) / millisecondsPerDay,
    )

    // ② 終了前でも、開催期間が30日以上なら常時掲載の落ち着いた色にする
    if (eventPeriodDays >= 30) {
      return 'always'
    }
  }

  // ③ 終了前かつ30日未満のイベントは緑色にする
  return 'spot'
}

/**
 * APIの日付文字列をJavaScriptのDateへ変換する
 * 「2026-08-04」のように時刻がない終了日は、その日の23:59:59として扱う
 */
const parseProjectDateTime = (value: string, useEndOfDay = false) => {
  const isDateOnly = /^\d{4}-\d{2}-\d{2}$/.test(value)
  const normalizedValue = value.includes(' ') ? value.replace(' ', 'T') : value
  const date = new Date(normalizedValue)

  if (Number.isNaN(date.getTime())) return null

  if (isDateOnly && useEndOfDay) {
    date.setHours(23, 59, 59, 999)
  }

  return date
}

/**
 * FullCalendarのendは終了日を含まないため、終了日の翌日（YYYY-MM-DD）を返す
 */
const getCalendarExclusiveEnd = (eventEndAt: string) => {
  const datePart = eventEndAt.match(/^\d{4}-\d{2}-\d{2}/)?.[0]
  if (!datePart) return eventEndAt

  const [year, month, day] = datePart.split('-').map(Number)
  const nextDay = new Date(year, month - 1, day + 1)
  const nextYear = nextDay.getFullYear()
  const nextMonth = String(nextDay.getMonth() + 1).padStart(2, '0')
  const nextDate = String(nextDay.getDate()).padStart(2, '0')

  return `${nextYear}-${nextMonth}-${nextDate}`
}


/**
 * プロジェクト記事を取得する
 */
const fetchProjects = async (start: string, end: string) => {
  const endpoint = baseUrl + '/project/calendar';
  // console.log('calendar api url:', endpoint)

  try {
    const response = await axios.get(endpoint, {
      params: {
        start,
        end,
      },
      withCredentials: true,
      headers: { Accept: 'application/json' },
    })
    return response.data
  } catch (error) {
    console.error('calendar api error:', error)
    return []
  }
}
</script>

<style lang="sass" scoped>

</style>
